import type { MetaSumInsights } from '@frontend/gql/social/graphql';

// Helper functions to check if specific data is null/undefined (not just zero) - Type-safe
export const isDataNull = (data: unknown): data is null | undefined => {
  return data === null || data === undefined;
};

// Type-safe helper for nested values (like Money objects with amount/currency)
export const isNestedValueNull = (data: unknown, field: keyof MetaSumInsights): boolean => {
  if (isDataNull(data)) return true;
  const typedData = data as MetaSumInsights;
  const value = typedData[field];
  if (isDataNull(value)) return true;
  // For nested objects like Money { amount: number, currency: string }
  if (typeof value === 'object' && value !== null) {
    const moneyValue = value as { amount?: number | null; currency?: string | null };
    if (isDataNull(moneyValue.amount)) return true;
  }
  return false;
};

// Check if highlight card data is missing (for fields like totalSpend, totalPurchase)
export const isHighlightDataMissing = (
  currentInterval: MetaSumInsights | null | undefined,
  field: keyof MetaSumInsights,
): boolean => {
  return isNestedValueNull(currentInterval, field);
};

// Check if metric data is missing (for simple number fields)
export const isMetricDataMissing = (
  currentInterval: MetaSumInsights | null | undefined,
  field: keyof MetaSumInsights,
): boolean => {
  if (isDataNull(currentInterval)) return true;
  return isDataNull(currentInterval[field]);
};

// Check if chart data is missing (check if we have any meaningful data points) - Type-safe
export const isChartDataMissing = (intervalInsights: unknown[] | null | undefined): boolean => {
  if (isDataNull(intervalInsights) || !Array.isArray(intervalInsights)) return true;
  return intervalInsights.length === 0;
};
